import React, { useCallback } from 'react';

import { Checkbox } from '@atlaskit/checkbox';

import { useDataBinding } from '../../hooks/useDataBinding.tsx';
import { useDynamicString, useDynamicBoolean } from '../../hooks/useDynamicValue.tsx';
import { useValidation } from '../../hooks/useValidation.tsx';
import { isDataBinding } from '../../types/A2UICommon.types.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';
import type { A2UICheckBoxProps } from '../../types/A2UIComponents.types.tsx';

type Props = {
	definition: A2UIComponentDefinition;
};

export const A2UICheckBox = ({ definition }: Props): React.JSX.Element => {
	const def = definition as A2UICheckBoxProps;
	const label = useDynamicString(def.label);
	const { isInvalid } = useValidation(def.checks);

	// Two-way binding for boolean value
	const bindingPath = isDataBinding(def.value) ? def.value.path : null;
	const [boundValue, setBoundValue] = useDataBinding<boolean>(bindingPath ?? '/___noop');
	const staticValue = useDynamicBoolean(def.value);

	const isChecked = bindingPath ? Boolean(boundValue) : staticValue;

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (bindingPath) {
				setBoundValue(e.target.checked);
			}
		},
		[bindingPath, setBoundValue],
	);

	return (
		<Checkbox
			label={label}
			isChecked={isChecked}
			onChange={handleChange}
			isInvalid={isInvalid}
			name={def.id}
		/>
	);
};
